import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from "@mui/material";
import {
  Close,
  OpenInFull,
  AttachFile,
  MicOff,
  Send,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { v4 as uuidv4 } from "uuid";
import { useChatbot } from "./chatBotContext";
import axios from "axios";
import CustomMarkdown from "core/components/global/markdown/markdown";

import { useAppTheme } from "core/contexts/theme/theme";
import { styled } from "styled-components";
import HistoryIcon from "assets/HistoryIcon";
import { RefreshIcon } from "assets/RefreshIcon";
import { IconChevronDown, IconChevronUp, IconSquareRoundedPlus } from "@tabler/icons-react";
import FullHistoryPanel from "./fullChatConvoHistory";
import MenuClose from "assets/MenuClose";
import MenuOpen from "assets/MenuOpen";
import { fetchBaseUrl } from "core/utils/common";
import VoiceChat from "core/components/global/input/voice/voiceChat";
import { set } from "rsuite/esm/internals/utils/date";

const baseURL = fetchBaseUrl({ port: 8001 });

type Capability = { name: string; description: string };
type Task = {
  name: string;
  description: string;
  capabilities: Capability[];
  task_type: string;
  supports_streaming: boolean;
};

type MessageType = 'user' | 'ai';
type Message = {
  id: string;
  type: MessageType;
  content: string;
  timestamp: Date;
};

type Conversation = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  is_active: boolean;
  message_count: number;
  metadata: any;
};

// Helper functions for date grouping
const formatDate = (date) => new Date(date);

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date) => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date) => {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return date > oneMonthAgo && !isPastWeek(date);
};

const groupConversationsByDate = (conversations: Conversation[]) => {
  const todayConversations: Conversation[] = [];
  const yesterdayConversations: Conversation[] = [];
  const pastWeekConversations: Conversation[] = [];
  const lastMonthConversations: Conversation[] = [];
  const olderConversations: Conversation[] = [];

  conversations.forEach((conversation) => {
    const conversationDate = formatDate(conversation.updated_at);

    if (isToday(conversationDate)) {
      todayConversations.push(conversation);
    } else if (isYesterday(conversationDate)) {
      yesterdayConversations.push(conversation);
    } else if (isPastWeek(conversationDate)) {
      pastWeekConversations.push(conversation);
    } else if (isPastMonth(conversationDate)) {
      lastMonthConversations.push(conversation);
    } else {
      olderConversations.push(conversation);
    }
  });

  return {
    todayConversations,
    yesterdayConversations,
    pastWeekConversations,
    lastMonthConversations,
    olderConversations,
  };
};


export default function SearchAgentSuite() {

  const [fullScreen, setFullScreen] = useState(false);
  const [viewMode, setViewMode] = useState("chat");
  const [searchType, setSearchType] = useState("kb");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const { isVisible, hideChatbot } = useChatbot();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const contentEditableRef = useRef<HTMLInputElement | null>(null);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskConversations, setTaskConversations] = useState<Record<string, MessageType[]>>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Conversation history states
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [todayConversations, setTodayConversations] = useState<Conversation[]>([]);
  const [yesterdayConversations, setYesterdayConversations] = useState<Conversation[]>([]);
  const [pastWeekConversations, setPastWeekConversations] = useState<Conversation[]>([]);
  const [lastMonthConversations, setLastMonthConversations] = useState<Conversation[]>([]);
  const [olderConversations, setOlderConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [highlightedConversation, setHighlightedConversation] = useState("");
  const [socket, setSocket] = useState<any>();
  const [taskMessagesMap, setTaskMessagesMap] = useState<{ [taskName: string]: Message[] }>({});
  const [showhistory, setShowhistory] = useState(false);
  const [hidden, setHidden] = useState<{ [id: string]: boolean }>({
    today: false,
    yesterday: true,
    pastWeek: true,
    lastMonth: true,
    older: true
  });

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axios.get(`${baseURL}/ac2/api/tasks`, {
          headers: {
            "X-API-Key": "dev-key"
          }
        });
        console.log("Tasks fetched:", response.data.tasks);
        setTasks(response.data.tasks);
        const chatTask = response.data.tasks.find((task: Task) => task.name === "chat");
        if (chatTask) {
          setSelectedTask(chatTask);
          setMessages(taskMessagesMap[chatTask.name] || []);
        }
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
      }
    };

    fetchTasks();

  }, []);

  useEffect(() => {
    console.log("Updated tasks:", tasks);
  }, [tasks]);



  const dispatch = useAppDispatch();
  const FormattedMessage = ({ message, className }: { message: string; className?: string }) => {
    console.log('Formatting message:', message);
    return (
      <div className={className}>
        <CustomMarkdown content={message} />
      </div>
    );
  };
  const FileUploadButton = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        console.log('Uploaded file:', file);

        try {
          const response = await axios.post(
            `${baseURL}/ac2/api/task/datalake_chat?stream=true`,
            {
              user_input: userPrompt,
              conversation_id: "string"
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'X-API-Key': 'dev-key'
              }
            }
          );

          // Add file upload response to conversation
          const responseData = response.data.response || response.data;
          const aiMessage: Message = {
            id: `ai-${Date.now()}`,
            type: 'ai',
            content: responseData || 'No response received from the server',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiMessage]);
          console.log('Success:', response.data);
        } catch (error) {
          console.error('Error:', error);
        }
      }
    };


    const triggerFileInput = () => {
      fileInputRef.current?.click();
    };

    return (
      <div>
        <AttachFile
          className="cursor-pointer"
          onClick={triggerFileInput}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    );
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${baseURL}/ac2/api/context/conversations`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'dev-key'
        }
      });

      console.log('Fetched conversations:', response.data);

      if (response.data && response.data.conversations) {
        setConversations(response.data.conversations);

        // Group conversations by date
        const {
          todayConversations,
          yesterdayConversations,
          pastWeekConversations,
          lastMonthConversations,
          olderConversations
        } = groupConversationsByDate(response.data.conversations);

        setTodayConversations(todayConversations);
        console.log("Today Convos", todayConversations);
        setYesterdayConversations(yesterdayConversations);
        setPastWeekConversations(pastWeekConversations);
        setLastMonthConversations(lastMonthConversations);
        setOlderConversations(olderConversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  // Load conversations when component mounts
  useEffect(() => {
    fetchConversations();
  }, []);


  const allowedTasks = ["chat", "knowledge_base_search", "datalake_chat"];
  const filteredTasks = tasks.filter((task): task is Task => allowedTasks.includes(task.name));

  const handleLabelClick = (label) => {
    setSelectedLabel(label === selectedLabel ? null : label);

    // If clicking on a time period category, toggle its visibility
    if (['today', 'yesterday', 'pastWeek', 'lastMonth', 'older'].includes(label)) {
      setHidden(prev => ({
        ...prev,
        [label]: !prev[label]
      }));
    }

    if (label === "refresh") {
      fetchConversations();
      setIsRefreshing(true);
      console.log("fetching convos")
      setTimeout(() => {
        setIsRefreshing(false);
      }, 2000);
    }
    if (label === "new") {
      setMessages([]);
      setShowhistory(false);
    }
  };
  const convoState = useAppSelector((state) => state.chatConversations);

  useEffect(() => {
    if (convoState.current) {
      setHighlightedConversation(convoState.current)
    }
  }, [convoState.current]);

  // Add this function inside your component
  const fetchConversationMessages = async (conversationId: string) => {
    try {
      const response = await axios.get(
        `${baseURL}/ac2/api/conversation/${conversationId}/messages`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'dev-key'
          }
        }
      );
      // Assuming response.data.messages is an array of messages
      // Map them to your Message type if needed
      const backendMessages = (response.data.messages || []).map((msg: any) => ({
        id: msg.id || `msg-${Date.now()}`,
        type: msg.role === "user" ? "user" : "ai",
        content: msg.content,
        timestamp: new Date(msg.timestamp)
      }));
      setMessages(backendMessages);
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      setMessages([
        {
          id: `error-${Date.now()}`,
          type: 'ai',
          content: 'Failed to load messages for this conversation.',
          timestamp: new Date()
        }
      ]);
    }
  };



  // Replace your current handleSelectConversation with this:
  const handleSelectConversation = (conversation: Conversation) => {
    setHighlightedConversation(conversation.id);
    setSelectedConversation(conversation.id);
    fetchConversationMessages(conversation.id);
  };


  const handleCloseAgent = () => {
    hideChatbot();
  }

  const [conversationId, setConversationId] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setConversationId(uuidv4());
    }
  }, []);

  const postData = async () => {

    if (!userPrompt.trim()) return;

    // Add user message to the conversation
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: userPrompt,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear input field
    setUserPrompt('');

    // Set loading state
    setIsLoading(true);

    try {
      const endpoint = selectedTask?.name !== "chat"
        ? `${baseURL}/ac2/api/task/${selectedTask?.name}?stream=true`
        : `${baseURL}/ac2/api/serve?stream=true`;

      const response = await axios.post(
        endpoint,
        {
          user_input: userMessage.content,
          conversation_id: conversationId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'dev-key'
          }
        }
      );

      // Get the response data
      const responseData = response.data?.response || response.data;
      console.log('Success:', response.data);
      console.log('Response content:', responseData);

      // Add AI response to conversation
      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: responseData || 'No response received from the server',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);

      // End loading state
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);

      // Add error message to conversation
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: 'ai',
        content: 'Sorry, there was an error processing your request.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      // End loading state
      setIsLoading(false);
    }

  };
  useEffect(() => {
    console.log("messages", messages);
  }, [messages]);


  useEffect(() => {
    if (selectedTask) {
      setMessages(taskMessagesMap[selectedTask.name] || []);
    }
  }, [selectedTask]);



  const { apptheme, toggleTheme } = useAppTheme();
  const ThemedIcon = styled.svg`
    stroke: ${({ theme }) => theme.colors.textColor};
  `;
  const isLight = apptheme.name === "light-theme";

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleVoiceInput = (text: string) => {
    setUserPrompt(text);
    // Optionally, focus the input bar:
    contentEditableRef.current?.focus();
  };

  const sendMessageByVoice = (data) => {
    if (contentEditableRef.current) {
      // Send the message because they said to
      postData();
    }
  }



  if (!isVisible) return null;
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${apptheme.name === "dark-theme" ? "backdrop-blur-md bg-gray-700 bg-opacity-10" : "backdrop-blur-md bg-purple-300 bg-opacity-40"}`}>
      <div
        className={` border border-black shadow-lg transition-all duration-300 ease-in-out ${fullScreen ? "w-screen h-screen" : "w-[90vw] h-[89vh]"
          } flex`}
      >
        {/* Sidebar */}
        <div
          className={`transition-all py-2 duration-300 ${apptheme.name === "dark-theme" ? "bg-[#212121] text-white" : "bg-[#ffffff] text-black"} ${!sidebarCollapsed ? "w-[300px]" : "w-[4vw]"}`}
        >
          <div className="">
            <div className="p-4 d-flex flex-column justify-content-start">
              <div className="cursor-pointer mb-4" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? <MenuClose /> : 
                <span className="d-flex flex-row items-center gap-2 mb-4 cursor-pointer">
                  <MenuOpen /><h3 className="text-base">Conversation</h3>
                </span>}
              </div>
              <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => handleLabelClick("refresh")}>
                <span className={isRefreshing ? "animate-spin" : ""}>
                  <RefreshIcon />
                </span>
                {!sidebarCollapsed && <h3 className="text-base">Refresh Conversations</h3>}
              </div>
              <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => handleLabelClick("new")}><IconSquareRoundedPlus />
                {!sidebarCollapsed && <h3 className="text-base">New Conversation</h3>}
              </div>
              <div className={`d-flex flex-row items-center gap-2 cursor-pointer ${sidebarCollapsed ? "w-[2vw] mt-3" : "mt-3"}`} onClick={() => setShowhistory(true)}><HistoryIcon />
                {!sidebarCollapsed && <h3 className="text-base">Full History</h3>}
              </div>
            </div>

            {!sidebarCollapsed && (
              <div
                className="px-1 hide-on-short-menu"
                style={{
                  overflowY: "auto",
                  position: "relative",
                  height: fullScreen
                    ? "calc(-338.8px + 110vh)"
                    : "calc(-338.8px + 99vh)"
                }}
              >

                <div className="d-flex flex-column cursor-pointer w-100 p-4 space-y-4">
                  {/* Today section */}
                  <div className="d-flex flex-column">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => handleLabelClick("today")}
                    >
                      <span className="text-base">Today</span>
                      {hidden.today ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                    </div>
                    {!hidden.today && todayConversations.length > 0 && (
                      <div className="mt-1 space-y-1">
                        {todayConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${apptheme.name === "dark-theme"
                              ? "hover:bg-gray-700 text-white"
                              : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                ? apptheme.name === "dark-theme"
                                  ? "bg-gray-700 text-white"
                                  : "bg-purple-100 text-black"
                                : ""
                              }`}
                            onClick={() => handleSelectConversation(conversation)}
                          >
                            {conversation.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {!hidden.today && todayConversations.length === 0 && (
                      <div className="ml-4 mt-1 p-2 text-sm text-gray-500">
                        No conversations today
                      </div>
                    )}
                  </div>

                  {/* Yesterday section */}
                  <div>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => handleLabelClick("yesterday")}
                    >
                      <span  className="text-base">Yesterday</span>
                      {hidden.yesterday ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                    </div>
                    {!hidden.yesterday && yesterdayConversations.length > 0 && (
                      <div className=" mt-1 space-y-1">
                        {yesterdayConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${apptheme.name === "dark-theme"
                              ? "hover:bg-gray-700 text-white"
                              : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                ? apptheme.name === "dark-theme"
                                  ? "bg-gray-700 text-white"
                                  : "bg-purple-100 text-black"
                                : ""
                              }`}
                            onClick={() => handleSelectConversation(conversation)}
                          >
                            {conversation.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {!hidden.yesterday && yesterdayConversations.length === 0 && (
                      <div className=" mt-1 p-2 text-sm text-gray-500">
                        No conversations yesterday
                      </div>
                    )}
                  </div>

                  {/* Past Week section */}
                  <div>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => handleLabelClick("pastWeek")}
                    >
                      <span  className="text-base">Past Week</span>
                      {hidden.pastWeek ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                    </div>
                    {!hidden.pastWeek && pastWeekConversations.length > 0 && (
                      <div className=" mt-1 space-y-1 max-h-48 overflow-y-auto pr-1">
                        {pastWeekConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${apptheme.name === "dark-theme"
                              ? "hover:bg-gray-700 text-white"
                              : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                ? apptheme.name === "dark-theme"
                                  ? "bg-gray-700 text-white"
                                  : "bg-purple-100 text-black"
                                : ""
                              }`}
                            onClick={() => handleSelectConversation(conversation)}
                          >
                            {conversation.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {!hidden.pastWeek && pastWeekConversations.length === 0 && (
                      <div className=" mt-1 p-2 text-sm text-gray-500">
                        No conversations from past week
                      </div>
                    )}
                  </div>

                  {/* Last Month section */}
                  <div>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => handleLabelClick("lastMonth")}
                    >
                      <span  className="text-base">Last Month</span>
                      {hidden.lastMonth ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                    </div>
                    {!hidden.lastMonth && lastMonthConversations.length > 0 && (
                      <div className=" mt-1 space-y-1 max-h-48 overflow-y-auto pr-1">
                        {lastMonthConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${apptheme.name === "dark-theme"
                              ? "hover:bg-gray-700 text-white"
                              : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                ? apptheme.name === "dark-theme"
                                  ? "bg-gray-700 text-white"
                                  : "bg-purple-100 text-black"
                                : ""
                              }`}
                            onClick={() => handleSelectConversation(conversation)}
                          >
                            {conversation.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {!hidden.lastMonth && lastMonthConversations.length === 0 && (
                      <div className=" mt-1 p-2 text-sm text-gray-500">
                        No conversations from last month
                      </div>
                    )}
                  </div>

                  {/* Older section */}
                  <div>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => handleLabelClick("older")}
                    >
                      <span  className="text-base">Older</span>
                      {hidden.older ? <IconChevronDown className="text-gray-500" /> : <IconChevronUp className="text-gray-500" />}
                    </div>
                    {!hidden.older && olderConversations.length > 0 && (
                      <div className=" mt-1 space-y-1 max-h-48 overflow-y-auto pr-1">
                        {olderConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${apptheme.name === "dark-theme"
                              ? "hover:bg-gray-700 text-white"
                              : "hover:bg-purple-100 text-black"} ${selectedConversation === conversation.id
                                ? apptheme.name === "dark-theme"
                                  ? "bg-gray-700 text-white"
                                  : "bg-purple-100 text-black"
                                : ""
                              }`}
                            onClick={() => handleSelectConversation(conversation)}
                          >
                            {conversation.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {!hidden.older && olderConversations.length === 0 && (
                      <div className=" mt-1 p-2 text-sm text-gray-500">
                        No older conversations
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col ${apptheme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"} overflow-hidden`}>
          {/* Header */}
          <div className={`flex justify-end ${apptheme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#faf5ff]"} px-6 pt-2 pb-2 `}>
            <div className="space-x-2 flex items-center">


              <IconButton onClick={() => setFullScreen(!fullScreen)}>
                <OpenInFull className="text-purple-600" />
              </IconButton>
              <IconButton onClick={handleCloseAgent}>
                <Close className="text-purple-600" />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          {viewMode === "chat" && (
            <div className={`flex-1 px-6 py-2 pb-2 space-y-4 ${!selectedTask ? "overflow-y-auto" : "overflow-y-auto"}`}>
              {showhistory ? (
                <FullHistoryPanel
                  isOpen={showhistory}
                  onClose={() => setShowhistory(false)}
                  conversations={conversations}
                  apptheme={apptheme}
                  onSelectConversation={fetchConversationMessages}
                  fetchConversations={fetchConversations}
                />
              ) : (
                <>
                  <div className={`z-2 t-0 sticky px-10 pt-6 pb-2 ${apptheme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"} sticky !important top-[-9px] !important`}>
                    <p
                      className={`font-bold mb-2 ${apptheme.name === "dark-theme" ? "text-white" : "text-black"}`}
                      style={{ textTransform: "none" }}
                    >
                      SEARCH AGENT SUITE
                    </p>
                    <FormControl className={`${apptheme.name === "dark-theme" ? "text-white" : "text-black"}`} component="fieldset">
                      <RadioGroup
                        row
                        value={selectedTask?.name || ""}
                        onChange={e => {
                          const task = filteredTasks.find((t: Task) => t.name === e.target.value);
                          setSelectedTask(task || null);
                          setMessages(task ? (taskMessagesMap[task.name] || []) : []);
                        }}
                      >
                        {filteredTasks.map((task: Task) => (
                          <FormControlLabel
                            key={task.name}
                            value={task.name}
                            control={
                              <Radio
                                sx={{
                                  color: "#a100ff",
                                  "&.Mui-checked": { color: "#a100ff" }
                                }}
                              />
                            }
                            label={task.name.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                            sx={{
                              marginRight: 4,
                              ".MuiFormControlLabel-label": {
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                letterSpacing: "0.02em"
                              }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <hr className="mt-4 border-t-2 border-gray-400" />
                  </div>

                  {/* Show messages for the selected task */}
                  {selectedTask && (
                    <div className="p-6 z-1">
                      <div className="transition-column mt-3 ml-[5.5rem] me-3 col-md-10 messages d-flex flex-column h-full overflow-auto flex-grow gap-[1.5rem]">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`p-4 rounded-lg shadow max-w-[35vw] ${msg.type === 'user'
                              ? `ml-auto ${apptheme.name === "dark-theme" ? "bg-[#212121] text-white" : "bg-purple-100 text-black"}`
                              : `mr-auto ${apptheme.name === "dark-theme" ? "bg-gray-600 text-white" : "bg-purple-100 text-black"}`
                              }`}
                          >
                            {msg.type === 'user' ? (
                              <p className="text-sm">
                                <CustomMarkdown content={msg.content} />
                              </p>
                            ) : (
                              <p className="text-sm">
                                <CustomMarkdown content={msg.content} />
                              </p>
                            )}
                            <div className="text-xs text-gray-500 mt-1 text-right">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </div>
                          </div>

                        ))}

                        {isLoading && (
                          <div className="p-4 bg-gray-100 rounded-lg max-w-[35vw] animate-pulse">
                            <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}





          {/* Footer Chat Input */}
          {!showhistory && <div className={`${apptheme.name === "dark-theme" ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]" : ""} p-4  `}>
            <div className={`flex items-center gap-2 ${apptheme.name === "dark-theme" ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]" : "bg-[#faf5ff]"} px-1 py-1 rounded-lg border mb-3 ml-5`}>
              <input
                ref={contentEditableRef}
                placeholder="Type your message..."
                className={`flex-1 outline-none border-none bg-transparent p-2 ${apptheme.name === "dark-theme" ? "text-white !important" : "text-black"} `}
                onChange={(e) => setUserPrompt(e.target.value)}
                value={userPrompt}
                onKeyPress={(e) => e.key === 'Enter' && postData()}
              />
              <div className="flex items-center gap-2 m-auto">
                <div className="m-auto">
                  <VoiceChat sendDataToParent={handleVoiceInput} sendMessageByVoice={sendMessageByVoice}/>
                </div>
                <IconButton onClick={postData} className={`${apptheme.name === "dark-theme" ? "text-white !important" : "text-black"}`}>
                  <Send />
                </IconButton>
              </div>
            </div>

          </div>}
          <div className={`text-right mt-2 ${apptheme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#F4EDF9]"}`}>
            <Button variant="text" color="primary" onClick={handleCloseAgent} >
              Close Agent Suite
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
